import { inject, Injectable } from '@angular/core';
import { isDefined, noop } from '@wm/core';
import { DB_CONSTANTS, initCaps, isEmptyObject, isNumberType, triggerFn } from '@wms/core';
import { DataModelDesignManagerService, ProjectService, ServiceFactoryService, TypeFactoryService } from '@wms/app/services';
import { inArray } from 'jquery';
import { cloneDeep, each, isEmpty, set, invert, find, filter, some, forEach, get, head, includes, intersection, isEqual, isNull, isUndefined, map, omitBy, sortBy } from 'lodash-es';
import { BaseComponent } from '@wms/app/components';
import { BaseVariableService, VARIABLE_CONSTANTS, VariableCrudManagerService } from '@wms/variables';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class WmsLiveVariableService extends BaseComponent {

  /*Set a flag based on whether the project is deployed or not.
 * 1. In the RUN mode, project is deployed.
 * 2. In the STUDIO mode, project has to be explicitly deployed.*/
  isProjectDeployed = false; // TODO: move to CONSTANTS.runMode
  isProjectDeployInProgress = false;
  projectDeployReqQueue: any;
  callbackParams = [];
  requestQueue = {};
  variableActive = {};
  invalidVariables: any = [];
  isDeployReqSourceChanged: any;
  packageDetails: any = {};
  emptyArr = [];
  isStudioMode = true; // TODO: move to constants
  dataModelDesignManagerService: any;
  typeFactoryService: any;
  varManagerService: any;
  dataTypes: any;
  variableInstance: any;
  serviceFactory: any;
  baseVariableService: any;
  entityVsTableNameMap: any = {};
  PROPERTY_PREFIX = 'wm_input_';
  propertyGroupChangeSubject = new Subject();

  constructor() {
    super();
    this.baseVariableService = inject(BaseVariableService);
    this.serviceFactory = inject(ServiceFactoryService);
    this.typeFactoryService = inject(TypeFactoryService);
    this.dataModelDesignManagerService = inject(DataModelDesignManagerService);
    this.varManagerService = inject(VariableCrudManagerService);
    this.dataTypes = this.typeFactoryService.getTypes();
  }

  /*Function to fetch the meta data for the table.*/
  __getTableMetaData(projectID: any, variable: any, writableVariable: any, options: any, callback: any, success?: any) {
    let tableDetails: any = {};

    /*Fetch the type nodes(consisting of the table & column details) for the database*/
    this.dataModelDesignManagerService.getDataModel(projectID, variable.liveSource, false, (database: any) => {
      let variableTable,
        variableType: any,
        firstPrimaryKey,
        tableNameToEntityNameMap: any = {},
        entityNameToTableNameMap: any = {},
        getJavaType = (javaType: any) => {
          if (DB_CONSTANTS.DATABASE_DATA_TYPES[javaType]) {
            javaType = DB_CONSTANTS.DATABASE_DATA_TYPES[javaType].java_type;
          } else if (DB_CONSTANTS.DATABASE_SECONDARY_DATA_TYPES[javaType]) {
            javaType = DB_CONSTANTS.DATABASE_DATA_TYPES[DB_CONSTANTS.DATABASE_SECONDARY_DATA_TYPES[javaType].java_type].java_type;
          }
          return javaType;
        };
      /*Clearing old relatedTables array so that it is populated with new values*/
      variable.relatedTables = [];
      variable.properties = [];
      forEach(database.tables, (table: any) => {
        tableNameToEntityNameMap[table.name] = table.entityName;
        entityNameToTableNameMap[table.entityName] = table.name;
      });
      variableType = entityNameToTableNameMap[variable.type];

      /*If the table that the variable associated with; no longer exists (i.e., it has been deleted or the name has been changed),
      * add the variable name to the list of invalid variables and return.*/
      if (!variableType) {
        this.invalidVariables.push(variable.name);
        return;
      }

      /*Loop through the tables*/
      forEach(database.tables, (table) => {

        let tableName = table.name,
          tablePackageName = database.packageName + "." + table.entityName,
          primaryKeys = table.primaryKey.columns || [],
          foreignKeys: any = [],
          columnsAdded: any = [];

        /*Add the "table-name" as a key to "tableDetails" and initialize "columnNames" and "relations".*/
        tableDetails[tableName] = {};
        tableDetails[tableName].columns = [];
        tableDetails[tableName].primaryFields = [];
        this.packageDetails[tablePackageName] = this.packageDetails[tablePackageName] || {};
        this.packageDetails[tablePackageName].primaryFields = [];
        /*Check if the current table is same as the table associated with the variable.*/
        if (tableName === variableType) {
          this.__setVariableProp(variable, writableVariable, "package", tablePackageName);
          this.__setVariableProp(variable, writableVariable, "tableName", table.name);
          this.__setVariableProp(variable, writableVariable, "tableType", isDefined(table.type) ? table.type : "TABLE");
        }
        /*Order the relations with priority as composite relations and one to one*/
        table.relations = sortBy(table.relations, (relation: any) => {
          return [relation.composite === false, relation.cardinality !== "OneToOne"];
        });
        /*Loop through the relations and insert the related columns*/
        forEach(table.relations, (relation) => {
          if (relation.primary) {
            let sourceCols = map(relation.mappings, (mapping) => {
              return mapping.sourceColumn;
            }),
              isPrimaryKey = intersection(sourceCols, primaryKeys).length > 0,
              newColumn: any,
              relatedCol: any,
              javaType,
              columnValue;
            /*Find out the foreign keys from the relations*/
            foreignKeys = foreignKeys.concat(sourceCols);
            /*Find the related column*/
            relatedCol = find(table.columns, (col) => {
              return col.name === sourceCols[0];
            });

            if (relatedCol.hidden) {
              columnsAdded = columnsAdded.concat(sourceCols);
              return;
            }
            javaType = getJavaType(relatedCol.javaType);
            newColumn = {
              'fieldName': relation.fieldName,
              'relatedColumnName': relatedCol.fieldName,
              'type': javaType,
              'fullyQualifiedType': javaType,
              'columnName': sourceCols.join(','),
              'isPrimaryKey': isPrimaryKey,
              'notNull': !relatedCol.nullable,
              'length': relatedCol.length,
              'precision': relatedCol.precision,
              'scale': relatedCol.scale,
              'generator': relatedCol.generatorType,
              'period': relatedCol.period,
              'isRelated': true,
              'defaultValue': get(relatedCol, ['columnValue', 'defaultValue']),
              'targetTable': relation.targetTable
            };
            columnValue = relatedCol.columnValue;
            if (columnValue && columnValue.type !== 'user-defined') {
              newColumn.systemInserted = newColumn.systemUpdated = true;
            }
            /*Removing properties with undefined or null values*/
            newColumn = omitBy(newColumn, (value) => {
              return isUndefined(value) || isNull(value);
            });
            /*If the column is already part of other relation, add readonly flag so that the it is not shown in liveform*/
            if (intersection(columnsAdded, sourceCols).length) {
              newColumn.readonly = true;
            }
            tableDetails[tableName].columns.push(newColumn);
            columnsAdded = columnsAdded.concat(sourceCols);
          }
        });
        /*Loop through the table*/
        forEach(table.columns, (column) => {
          /*Hidden columns should not be included in properties map*/
          if (column.hidden) {
            return;
          }
          /*Columns names are present in primary keys. Map the respective field names*/
          if (includes(table.primaryKey.columns, column.name)) {
            tableDetails[tableName].primaryFields.push(column.fieldName);
            this.packageDetails[tablePackageName].primaryFields.push(column.fieldName);
          }
          if (!includes(columnsAdded, column.name)) {
            let javaType = getJavaType(column.javaType),
              isPrimaryKey = includes(primaryKeys, column.name),
              isForeignKey = includes(foreignKeys, column.name),
              newColumn: any = {
                "fieldName": column.fieldName,
                "type": javaType,
                "fullyQualifiedType": javaType,
                "columnName": column.name,
                "isPrimaryKey": isPrimaryKey,
                "notNull": !column.nullable,
                "length": column.length,
                "precision": column.precision,
                "scale": column.scale,
                "generator": column.generatorType,
                "period": column.period,
                "isRelated": isForeignKey,
                "defaultValue": get(column, ['columnValue', 'defaultValue'])
              },
              columnValue = column.columnValue;
            if (columnValue && columnValue.type !== 'user-defined') {
              newColumn.systemInserted = newColumn.systemUpdated = true;
            }
            /*Removing properties with undefined or null values*/
            newColumn = omitBy(newColumn, (value) => {
              return isUndefined(value) || isNull(value);
            });
            tableDetails[tableName].columns.push(newColumn);
          }
        });

        forEach(table.relations, (relation) => {
          let relatedColumns: any = [];
          /*Find out the related columns for the relation*/
          each(relation.mappings, (mapping) => {
            if (relation.primary) {
              relatedColumns.push(mapping.sourceColumn);
            }
          });
          /*Loop through the table's columns*/
          forEach(tableDetails[tableName].columns, (column) => {

            let fkColumnName = column.columnName.split(',');
            /*Check if the column is a foreign-key/related column and it is present in the "relatedColumns" of the current relation.*/
            if (column.isRelated && isEqual(relatedColumns, fkColumnName)) {

              /*Check if the current table is same as the table associated with the variable.*/
              if (tableName === variableType) {
                /*Add the related table name to the variable.properties attribute so that complete data for the table could be fetched.
                 * Following are the cases handled.
                 * 1. Add the related table name only if it has not been added already. This case might arise if there are multiple relations
                 * from Table1 to Table2
                 * 2. Do not add the related table name if it is same as the current table. This case might arise if there is a relation from TableX to TableX itself(i.e., self-reference).*/

                /*Reset the "columnName" to the "relatedType" so that the "relatedType" is used for Display & CRUD operations.*/
                /*column.columnName = column.fieldName;
                column.fieldName = tableNameToEntityNameMap[relation.relatedType];
                column.fieldName = column.fieldName.charAt(0).toLowerCase() + column.fieldName.slice(1);*/
                column.relatedTableName = relation.targetTable;
                column.relatedEntityName = tableNameToEntityNameMap[relation.targetTable];
                if ((inArray(relation.fieldName, variable.properties) === -1)) {
                  variable.properties.push(relation.fieldName);
                  variable.relatedTables.push({
                    "columnName": column.fieldName,
                    "relationName": relation.fieldName,
                    "type": tableNameToEntityNameMap[relation.targetTable],
                    "package": tablePackageName,
                    "watchOn": initCaps(variable.liveSource) + tableNameToEntityNameMap[relation.targetTable] + "Data"
                  });
                }
              }
            }
          });
        });
      });
      this.__setVariableProp(variable, writableVariable, 'properties', (variable.properties || []));
      this.__setVariableProp(variable, writableVariable, 'relatedTables', (variable.relatedTables || []));
      tableDetails[variableType].entityName = variable.type;
      tableDetails[variableType].fullyQualifiedName = variable.package;
      tableDetails[variableType].tableType = variable.tableType;
      variableTable = tableDetails[variableType];

      /*Iterate through the columns of the table associated with the variable and fetch all the columns of the related tables.*/
      forEach(variableTable.columns, (column) => {
        let columnDef;
        /*Fetch all the columns of the related table and set them as columns in the related column.*/
        if (column.isRelated) {
          /*Since "columns" may contain a reference to itself, "copy" is being used.
          * This is done because there is a "copy" done in Variables.store.*/
          column.columns = cloneDeep(tableDetails[column.relatedTableName].columns);
          column.relatedFieldName = column.fieldName + "." + tableDetails[column.relatedTableName].primaryFields[0];
          if (this.dataTypes && this.dataTypes[variable.package]) {
            columnDef = this.dataTypes[variable.package].fields[column.fieldName];
            column.isList = columnDef ? columnDef.isList : false;
          }
        }
      });

      this.__setVariableProp(variable, writableVariable, 'propertiesMap', tableDetails[variableType]);

      if (writableVariable && writableVariable._isNew) {
        //For new variable, if orderby is not set, set the default orderby as primary field with ascending order
        firstPrimaryKey = head(variable.propertiesMap.primaryFields);
        if (!writableVariable.orderBy && firstPrimaryKey) {
          this.__setVariableProp(variable, writableVariable, 'orderBy', firstPrimaryKey + ' asc');
        }
        variable._isNew = writableVariable._isNew = false;
      }
      triggerFn(callback, projectID, variable, options, success);
    }, noop);
  }

  __setVariableProp(variable: any, writableVariable: any, propertyName: any, propertyValue: any) {
    variable[propertyName] = propertyValue;
    /*Check for sanity.
    * writableVariable will be null in case of partial page variables.*/
    if (writableVariable && !isEqual(writableVariable[propertyName], propertyValue)) {
      /*Set the "saveVariables" to true so that when "save"/"run" buttons are clicked, the variables could be saved into the file.*/
      //This flag has to be enabled for users with edit page permissions
      if (this.preferencesService.get('project.pages.edit')) {
        // TODO: *** refactoring check
        // this.root.saveVariables = true;
      }
      writableVariable[propertyName] = propertyValue;
      this.baseVariableService.updateVariable(writableVariable.name, writableVariable);
    }
  }

  /*Function to fetch records*/
  __update(variable: any, varInstance: any, options?: any) {
    options = options || {};

    this.__getData(variable, varInstance, options);
  }

  __execute(variable: any, varInstance: any, options: any) {
    /* put the variable name into the variable object */

    // /*In the "Studio" mode, the entity meta data is read, the properties are updated in the variable;
    //  * so that the variables.json file is updated on "save".
    //  * 1. In the "Run"/"Application" mode, all the properties are fetched from the variable read from the file itself
    //  * (as "getDataModel" call would not work in the "Application" mode).
    //  * 2. In case of variables inside imported prefabs, "getDataModel" call would not work
    //  * because the data-model would not be present in the project services directly.*/
    if (!variable._prefabName && !variable._partialname) {
      /* get studio copy of variable*/
      const writableVariable = this.baseVariableService.getVariableByName(variable.name);
      this.__getTableMetaData(this.projectService.getProjectId(), variable, writableVariable, options, () => {
        /*For variables of all operations, update the dataSet with the "propertiesMap" only.*/
        this.__updateVariableDataset(variable, [], variable.propertiesMap, {});
        Object.assign(varInstance, variable); // assign propertyMap info to varInstance
        // For app var, make create call once propertiesMap is set
        if (variable.owner === 'App' && this.baseVariableService.CRUDMAP['CREATE']['App'].length) {
          this.varManagerService.invokeSave(false, false, true);
        }
      });
    }
  }

  /*function to get the data associated with the live variable*/
  __getData(variable: any, varInstance: any, options: any) {
    /*Check for sanity of the "variable".
    * Studio Mode: Also, invoke the service to get the data of the variable only if the "liveSource" still exists in the project's databases.
    * If the database has been deleted from the project, then prevent sending of the request.
    * Run Mode: Invoke the service to get the variable data.*/
    if (!isEmptyObject(variable) && (variable._prefabName)) {
      this.__execute(variable, varInstance, options);
    } else if (!isEmptyObject(variable)) {
      (this.serviceFactory.getServicesWithType() as Promise<any>).then((services) => {
        /*Checking for existence of variable's live source in databases available*/
        const varInfo = filter(services, (item: any) => {
          return (item.name === variable.liveSource && item.type === 'DataService');
        });
        if (varInfo.length) {
          this.__execute(variable, varInstance, options);
        }
      });
    }
  }

  __updateVariableDataset(variable: any, data: any, propertiesMap: any, pagingOptions: any) {
    variable.dataSet = {
      'data': data,
      'propertiesMap': propertiesMap,
      'pagingOptions': pagingOptions
    };
  }

  __getVariableDependencies() {
    let selectedVariable: any = this.baseVariableService.selectedVariable;
    /*get the list of services available and append the options for service property*/
    (this.serviceFactory.getServicesWithType() as Promise<any>).then((services: any) => {
      let dataServicesObj: any = {};
      services.sort((prevItem: any, currentItem: any) => prevItem.name.localeCompare(currentItem.name))
      /* iterating over mixed services returned to collect names of DataServices */
      services.forEach((serviceObj: any) => {
        if (serviceObj.type === VARIABLE_CONSTANTS.SERVICE_TYPE_DATA) {
          dataServicesObj[serviceObj.name] = serviceObj.name;
        }
      });

      /* assign the data services array to the options of live source property */
      selectedVariable.propertyObj.liveSource.options = dataServicesObj;
      if (selectedVariable.isNew) {
        this.__resetOrderByProp(selectedVariable.propertyObj);
      }
      //reset the order by widget display value as it is not a part of modelObj
      set(selectedVariable, 'propertyObj.orderBy.typeaheadvalue', '');
      /*append the options for types property based on selected service property (if any)*/
      if (selectedVariable.variableObject.liveSource) {
        this.__onPropertyChange('liveSource', undefined, true);
      } else {
        this.baseVariableService.sourceExists();
      }
    });
  }

  /*function to handle property change for a variable*/
  __onPropertyChange(propertyName: string, value?: any, initFlag?: any) {
    if (!initFlag) {
      this.baseVariableService.markUpdated();
    }
    /*If 'service' property changed, change the operation property as well*/
    if (propertyName === 'liveSource') {
      this.__onLiveSourceChange(initFlag);
    } else if (propertyName === 'type') {
      this.__onTypeChange(initFlag);
    } else if (propertyName === 'operation') {
      this.__onOperationChange(initFlag);
    } else if (propertyName === 'maxResults') {
      this.__onMaxResultsChange();
    } else if (VARIABLE_CONSTANTS.EVENTS.indexOf(propertyName) > -1) {
      this.baseVariableService.onEventChange(propertyName, value);
    }
  }

  __onMaxResultsChange() {
    this.baseVariableService.selectedVariable.variableObject.maxResults = this.baseVariableService.selectedVariable.variableObject.maxResults || 20;
  }

  __onOperationChange(initFlag: any) {
    let selectedVariable: any = this.baseVariableService.selectedVariable;
    let updateFlag;
    updateFlag = selectedVariable.variableObject.operation === 'read';
    selectedVariable.variableObject.autoUpdate = updateFlag;
    selectedVariable.variableObject.startUpdate = updateFlag;
    this.__onTypeChange(initFlag);
  }

  __onTypeChange(initFlag: any) {
    let selectedVariable: any = this.baseVariableService.selectedVariable;
    let selectedTable,
      propertyObject = selectedVariable.propertyObj,
      modifiedFieldName,
      operation: any,
      orderByProp: any,
      oldBindings: any = {};
    /* If the 'type' property changed, change the data properties */
    selectedTable = this.entityVsTableNameMap && this.entityVsTableNameMap[selectedVariable.variableObject['type']];
    operation = selectedVariable.variableObject.operation;
    //reset the orderby property
    if (!initFlag) {
      this.__resetOrderByProp(propertyObject);
    }

    /*check value sanity*/
    if (!selectedTable || selectedTable === '') {
      return;
    }

    /* get the selected table's columns */
    this.dataModelDesignManagerService.getEntityForTable({
      projectId: this.projectService.getProjectId(),
      dataModelName: selectedVariable.variableObject['liveSource'],
      tableName: selectedTable
    }, (response: any) => {
      let subGroupName = operation === 'read' ? 'filterfields' : 'inputfields',
        emptySubGroupName = operation === 'read' ? 'inputfields' : 'filterfields',
        dataSetSubgroup: any,
        relatedFields: any,
        emptyDataSetSubgroup: any;
      /* find the dataset subgroup out of properties */
      this.baseVariableService.propertyGroups.forEach((parentGroup: any) => {
        parentGroup.subGroups.forEach((subGroup: any) => {
          if (subGroup.name === subGroupName) {
            dataSetSubgroup = subGroup;
          }
          if (subGroup.name === emptySubGroupName) {
            emptyDataSetSubgroup = subGroup;
          }
        });
      });

      /* remove properties from the dataset subgroups */
      dataSetSubgroup.properties = [];
      emptyDataSetSubgroup.properties = [];

      /*remove properties for previous selected operation's params*/
      Object.keys(selectedVariable.propertyObj).forEach((propertyName: any) => {
        let propertyObject = selectedVariable.propertyObj[propertyName];
        if (propertyObject.dataBinding) {
          modifiedFieldName = this.PROPERTY_PREFIX + propertyName;
          delete selectedVariable.propertyObj[modifiedFieldName];
          selectedVariable.propertyNames.splice(selectedVariable.propertyNames.indexOf(modifiedFieldName), 1);
        }
      });

      /* empty previous dataBinding fields */
      if (!initFlag) {
        /*initialize the data binding object for the variable*/
        selectedVariable.variableObject.dataBinding = [];
      } else if (typeof selectedVariable.variableObject.dataBinding === 'object' && !Array.isArray(selectedVariable.variableObject.dataBinding)) {
        /*
          * In old implementation, dataBinding is an object map like:
          * dataBinding: {"field1": "value1", "field2": "value2}
          * If such object is found, extract it and persist for migration
          */
        oldBindings = selectedVariable.variableObject.dataBinding;
        selectedVariable.variableObject.dataBinding = [];
      }

      /* create actual store for the variable, here the values will be kept resulting out of binding */
      if (operation === 'read') {
        selectedVariable.variableObject.filterFields = {};
      } else {
        selectedVariable.variableObject.inputFields = {};
      }

      /* loop over each column to insert into variable properties */
      response.columns.forEach((column: any) => {
        /* if variable's operation is 'delete' and table has primary keys then process only the primary keys
          * if variable's operation is 'insert/update' and column is not user-defined then exclude them
          * */
        if (this.__isValidColumn(operation, response.primaryKey.columns, column)) {
          var fieldType = isNumberType(column.sqlType) ? 'number' : column.sqlType,
            paramReference,
            paramsCollection = selectedVariable.variableObject.dataBinding;

          paramsCollection.forEach((paramObject: any) => {
            if (paramObject.target === column.fieldName) {
              paramReference = paramObject;
            }
          });
          /* reference not found, add to the fields collection and process internal nodes based on type meta info for param */
          if (!paramReference) {
            paramReference = paramsCollection[paramsCollection.push({
              "target": column.fieldName,
              "type": fieldType
            }) - 1];
            if (oldBindings[column.fieldName] && typeof oldBindings[column.fieldName] !== 'object') {
              paramReference.value = oldBindings[column.fieldName];
            }
          }
        }
      });

      if (operation === 'insert' || operation === 'update') {
        this.__addOneToManyRelatedFieldNodes(response, operation, selectedVariable);
      }

      /*Removing columns not found in new column definitions(columns deleted while export/reImport)*/
      relatedFields = map(response.relations, 'fieldName');
      selectedVariable.variableObject.dataBinding = filter(selectedVariable.variableObject.dataBinding, (field: any) => {
        return field.target === 'dataBinding' || some(response.columns, (column: any) => {
          return column.fieldName === field.target || includes(relatedFields, field.target);//Retaining old binding objects
        });
      });

      /*flag to determine if the operation property of live-variable should be disabled or enabled
        * based on the value that the table type is view or table*/
      selectedVariable.propertyObj["operation"].disabled = (response.type === "VIEW") || (selectedVariable.propertyObj["operation"].isprimary && !this.baseVariableService.createOnly);

      this.baseVariableService.addBindingsToProperties();

      selectedVariable.propertyObj.orderBy.options = {};

      orderByProp = selectedVariable.propertyObj.orderBy;
      if (selectedVariable.packageNameMap) {
        let selectedTablePackage = selectedVariable.packageNameMap[selectedVariable.variableObject.type],
          fields = this.typeFactoryService.getFieldsForType(selectedTablePackage),
          orderByVal = orderByProp.modelObj[orderByProp.modelKey],
          rawFieldOptions = orderByVal ? orderByVal.split(',') : [],
          excludeOptions: any = [];

        // set package name to the variable, for meta data generation in binding dialog.
        selectedVariable.variableObject.package = selectedTablePackage;

        orderByProp.value = [];
        rawFieldOptions.forEach((field: any, index: any) => {
          if (!field) {
            return;
          }
          var fieldSplit = field.trim().split(' ');
          orderByProp.value.push({
            'key': fieldSplit[0],
            'order': fieldSplit[1],
            'index': index,
            'isInvalid': !includes(fields, fieldSplit[0])
          });
          excludeOptions.push(field.split(' ')[0]);
        });
        orderByProp.options = {};
        forEach(fields, (field: any) => {
          if (includes(excludeOptions, field)) {
            return;
          }
          orderByProp.options[field] = field;
        });
      }
    });
    selectedVariable.variableObject.criteriaFilterFields = this.typeFactoryService.getFieldsForType(selectedVariable.variableObject.package, { 'filter': 'terminal', 'getFieldInfo': true });
    this.propertyGroupChangeSubject.next(true);
  }

  /* check if the operation is insert or update
  loop over each relation and for all one to many relations insert it into variable properties */
  __addOneToManyRelatedFieldNodes(response: any, operation: any, selectedVariable: any) {
    response.relations.forEach((relation: any) => {
      if (relation.cardinality === 'OneToMany') {
        var fieldType = selectedVariable.packageNameMap[(invert(this.entityVsTableNameMap))[relation.targetTable]],
          paramReference,
          paramsCollection = selectedVariable.variableObject.dataBinding;

        paramsCollection.forEach((paramObject: any) => {
          if (paramObject.target === relation.fieldName) {
            paramReference = paramObject;
          }
        });
        /* reference not found, add to the fields collection and process internal nodes based on type meta info for param */
        if (!paramReference) {
          paramReference = paramsCollection[paramsCollection.push({
            "target": relation.fieldName,
            "type": fieldType,
            "isList": true
          }) - 1];
        }
      }
    });
  }

  //Filtering fields based on operation
  __isValidColumn(operation: any, primaryKeys: any, column: any) {
    if (column.hidden) {
      return false;
    }
    if (operation === 'delete') {
      return includes(primaryKeys, column.name) || isEmpty(primaryKeys);
    }
    return !this.__isSystemDefined(column.columnValue) || operation === 'read';
  }

  __isSystemDefined(colValue: any) {
    return colValue && colValue.type !== 'user-defined';
  }


  __resetOrderByProp(propertyObject: any) {
    if (!propertyObject.orderBy) {
      return;
    }
    propertyObject.orderBy.typeaheadvalue = '';
    propertyObject.orderBy.options = {};
    propertyObject.orderBy.value = [];
    propertyObject.orderBy.modelObj[propertyObject.orderBy.modelKey] = '';
  }

  __onLiveSourceChange(initFlag: any) {
    let selectedVariable: any = this.baseVariableService.selectedVariable;
    let selectedDatabase: any;
    let propertyObject = selectedVariable.propertyObj;
    if (!initFlag) {
      propertyObject.type.modelObj[selectedVariable.propertyObj.type.modelKey] = '';
      propertyObject.type.options = [];
      this.__resetOrderByProp(propertyObject);
    }
    selectedDatabase = selectedVariable.variableObject['liveSource'];

    if (selectedDatabase) {
      this.dataModelDesignManagerService.getDataModel(this.projectService.getProjectId(), selectedDatabase, false, (datamodel: any) => {
        let dataTables = datamodel.tables;
        selectedVariable.propertyObj.type.options = {};
        selectedVariable.packageNameMap = {};
        dataTables.sort((prevItem: any, currentItem: any) => prevItem.entityName.localeCompare(currentItem.entityName))
        dataTables.forEach((dataTable: any) => {
          this.entityVsTableNameMap[dataTable.entityName] = dataTable.name;
          selectedVariable.propertyObj.type.options[dataTable.entityName] = dataTable.entityName;
          selectedVariable.packageNameMap[dataTable.entityName] = dataTable.fullyQualifiedName;
        });

        if (initFlag && selectedVariable.variableObject['type'] && selectedVariable.variableObject['type'] !== '') {
          this.__onPropertyChange('type', undefined, true);
        }
        this.baseVariableService.sourceExists();
      }, (error: any) => {
        selectedVariable.serviceError = true;
        selectedVariable.serviceErrorMessage = error;
      });
    }
  }
}
